import React, { useMemo, useState } from "react";

import PageTitle from "../../../layouts/PageTitle";
import { useGetNcsMyDailyQuery } from "../../../../store/appApi";
import { panelClass, fieldClass, labelClass, fmtDate, fmtDateInput } from "./config";

const WHITE_FIELD_STYLE = { backgroundColor: "#fff", color: "#1f2937" };

const monthStart = () => {
  const d = new Date();
  return fmtDateInput(new Date(d.getFullYear(), d.getMonth(), 1));
};

const fmtTime = (d) =>
  d
    ? new Date(d).toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
      })
    : "";

// "My Daily" — an employee's own work-done-by-them report, date-wise. Same
// underlying rollup as the super-admin "Employee Daily" screen, but scoped to
// the signed-in user by the server (not a partner/employee picker here — this
// is a personal report, not a team one). Clicking a count cell opens a modal
// listing the customers behind that number, instead of an inline expand.
const NcsMyDaily = () => {
  const [from, setFrom] = useState(monthStart());
  const [to, setTo] = useState(fmtDateInput());
  const [drill, setDrill] = useState(null); // { date, actionKey, actionLabel, items }

  const { data, isFetching } = useGetNcsMyDailyQuery({ from, to });

  const actions = useMemo(() => data?.actions || [], [data]);
  const rows = useMemo(
    () => [...(data?.rows || [])].sort((a, b) => b.date.localeCompare(a.date)),
    [data],
  );
  const grandTotal = data?.grandTotal;

  const showOther = useMemo(
    () =>
      (grandTotal?.byAction?.other || 0) > 0 ||
      rows.some((r) => (r.byAction?.other || 0) > 0),
    [grandTotal, rows],
  );

  const cols = useMemo(
    () => [...actions, ...(showOther ? [{ key: "other", label: "Other" }] : [])],
    [actions, showOther],
  );

  const openDrill = (row, col) => {
    const items = (row.items || []).filter((it) => {
      const k = actions.some((a) => a.key === it.action) ? it.action : "other";
      return k === col.key;
    });
    setDrill({ date: row.date, actionKey: col.key, actionLabel: col.label, items });
  };

  const colCount = 2 + cols.length;

  return (
    <>
      <PageTitle activeMenu="My Daily Report" motherMenu="Netplus" />
      <section className={panelClass}>
        <div className="flex flex-wrap items-end gap-3 border-b border-gray-100 px-5 py-3">
          <div>
            <span className={labelClass}>From</span>
            <input
              type="date"
              className={`${fieldClass} w-40`}
              style={WHITE_FIELD_STYLE}
              value={from}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
            />
          </div>
          <div>
            <span className={labelClass}>To</span>
            <input
              type="date"
              className={`${fieldClass} w-40`}
              style={WHITE_FIELD_STYLE}
              value={to}
              min={from}
              max={fmtDateInput()}
              onChange={(e) => setTo(e.target.value)}
            />
          </div>
          {grandTotal && (
            <div className="ml-auto text-sm text-gray-500">
              Total this range:{" "}
              <span className="font-semibold text-cyan-700">{grandTotal.total}</span>
            </div>
          )}
        </div>

        <p className="px-5 pt-3 text-xs text-gray-400">
          Click a number below to see which customers it's for.
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500">
                <th className="px-4 py-3 text-left">Day</th>
                {cols.map((c) => (
                  <th key={c.key} className="px-3 py-3 text-center">
                    {c.label}
                  </th>
                ))}
                <th className="px-4 py-3 text-center">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isFetching ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-12 text-center text-gray-400">
                    Loading…
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-12 text-center text-gray-400">
                    No logged activity in this range.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.date} className="hover:bg-gray-50">
                    <td className="px-4 py-2.5 font-medium text-gray-700">
                      {fmtDate(r.date)}
                    </td>
                    {cols.map((c) => {
                      const n = r.byAction?.[c.key] || 0;
                      return (
                        <td key={c.key} className="px-3 py-2.5 text-center tabular-nums">
                          {n ? (
                            <button
                              type="button"
                              className="rounded-md px-2 py-0.5 font-semibold text-cyan-700 underline decoration-dotted hover:bg-cyan-50"
                              onClick={() => openDrill(r, c)}
                            >
                              {n}
                            </button>
                          ) : (
                            <span className="text-gray-300">0</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="px-4 py-2.5 text-center font-semibold tabular-nums text-gray-800">
                      {r.total}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {grandTotal && rows.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-300 bg-gray-100 font-bold text-gray-800">
                  <td className="px-4 py-3 text-xs uppercase tracking-wider text-gray-500">
                    Grand total
                  </td>
                  {cols.map((c) => (
                    <td key={c.key} className="px-3 py-3 text-center tabular-nums">
                      {grandTotal.byAction?.[c.key] || 0}
                    </td>
                  ))}
                  <td className="px-4 py-3 text-center tabular-nums">{grandTotal.total}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>

      {drill && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDrill(null)}
        >
          <div
            className="max-h-[80vh] w-full max-w-lg overflow-hidden rounded-xl bg-white shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-100 px-5 py-3">
              <div>
                <div className="text-sm font-semibold text-gray-900">{drill.actionLabel}</div>
                <div className="text-xs text-gray-400">{fmtDate(drill.date)}</div>
              </div>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setDrill(null)}
              >
                ✕
              </button>
            </div>
            <div className="max-h-96 overflow-y-auto">
              {drill.items.length === 0 ? (
                <div className="px-5 py-6 text-center text-sm text-gray-400">
                  No detail rows.
                </div>
              ) : (
                drill.items.map((it, i) => (
                  <div
                    key={i}
                    className={`flex items-start gap-3 px-5 py-2.5 text-sm ${
                      i % 2 ? "bg-gray-50" : "bg-white"
                    }`}
                  >
                    <span className="w-12 shrink-0 text-xs text-gray-400">{fmtTime(it.at)}</span>
                    <span className="text-gray-700">
                      {it.summary || it.customerKey || "—"}
                      {it.customerKey && it.summary ? (
                        <span className="ml-1 text-xs text-gray-400">({it.customerKey})</span>
                      ) : null}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default NcsMyDaily;
